import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { useActivityLogger } from "@/hooks/useActivityLogger";

/**
 * Shopping cart for course bundles (Phase 1).
 *
 * Owns one ACTIVE cart per user. Items reference courses; pricing is NEVER
 * read from the client — Phase 3 will add a server `compute-cart-total` edge
 * function. The client only displays prices for UX.
 */

const CART_KEY = (userId: string | undefined) => ["cart", userId];

export interface CartItem {
  id: string;
  cart_id: string;
  course_id: string;
  added_at: string;
  course?: {
    id: string;
    slug: string;
    title: string;
    price: number | null;
    thumbnail_url: string | null;
    is_free: boolean | null;
  } | null;
}

interface CartState {
  cartId: string | null;
  items: CartItem[];
}

const EMPTY: CartState = { cartId: null, items: [] };

async function ensureActiveCart(userId: string): Promise<string> {
  const { data: existing, error: selectErr } = await supabase
    .from("carts")
    .select("id")
    .eq("user_id", userId)
    .eq("status", "active")
    .maybeSingle();
  if (selectErr) throw selectErr;
  if (existing?.id) return existing.id;

  const { data: created, error: insertErr } = await supabase
    .from("carts")
    .insert({ user_id: userId, status: "active" })
    .select("id")
    .single();
  if (insertErr) throw insertErr;
  return created.id;
}

export function useCart() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { logActivity } = useActivityLogger();

  const query = useQuery<CartState>({
    queryKey: CART_KEY(user?.id),
    queryFn: async () => {
      if (!user) return EMPTY;
      const { data: cart, error: cartErr } = await supabase
        .from("carts")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();
      if (cartErr) throw cartErr;
      if (!cart) return EMPTY;

      const { data: items, error: itemsErr } = await supabase
        .from("cart_items")
        .select(
          "id, cart_id, course_id, added_at, course:courses(id, slug, title, price, thumbnail_url, is_free)"
        )
        .eq("cart_id", cart.id)
        .order("added_at", { ascending: true });
      if (itemsErr) throw itemsErr;

      return { cartId: cart.id, items: (items ?? []) as CartItem[] };
    },
    enabled: Boolean(user),
    staleTime: 30_000,
  });

  const addItem = useMutation({
    mutationFn: async (courseId: string) => {
      if (!user) throw new Error("Not authenticated");
      const cartId = await ensureActiveCart(user.id);
      const { error } = await supabase
        .from("cart_items")
        .insert({ cart_id: cartId, course_id: courseId });
      if (error) {
        // Unique violation == already in cart, treat as success.
        if (error.code !== "23505") throw error;
      }
      return { courseId };
    },
    onSuccess: ({ courseId }) => {
      qc.invalidateQueries({ queryKey: CART_KEY(user?.id) });
      void logActivity("cart_add", { action: "cart_add", target_type: "course", target_id: courseId });
      toast({ title: "Adicionado ao carrinho", duration: 1800 });
    },
    onError: (err: any) => {
      toast({
        title: "Não foi possível adicionar",
        description: err?.message,
        variant: "destructive",
      });
    },
  });

  const addManyItems = useMutation({
    mutationFn: async ({ courseIds, bundleId }: { courseIds: string[]; bundleId?: string }) => {
      if (!user) throw new Error("Not authenticated");
      if (courseIds.length === 0) return { added: 0, courseIds: [], bundleId };
      const cartId = await ensureActiveCart(user.id);
      const rows = courseIds.map((course_id) => ({ cart_id: cartId, course_id }));
      const { error } = await supabase
        .from("cart_items")
        .upsert(rows, { onConflict: "cart_id,course_id", ignoreDuplicates: true });
      if (error) throw error;
      return { added: courseIds.length, courseIds, bundleId };
    },
    onSuccess: ({ added, bundleId }) => {
      qc.invalidateQueries({ queryKey: CART_KEY(user?.id) });
      void logActivity("cart_add_bundle", {
        action: "cart_add_bundle",
        target_type: "course_bundle",
        target_id: bundleId ?? "many",
        new_value: { count: added },
      });
      toast({
        title: bundleId ? "Pacote adicionado ao carrinho" : "Cursos adicionados",
        description: `${added} curso${added === 1 ? "" : "s"}.`,
        duration: 2000,
      });
    },
    onError: (err: any) => {
      toast({
        title: "Não foi possível adicionar o pacote",
        description: err?.message,
        variant: "destructive",
      });
    },
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      if (!user) throw new Error("Not authenticated");
      const item = query.data?.items.find((i) => i.id === itemId);
      const { error } = await supabase.from("cart_items").delete().eq("id", itemId);
      if (error) throw error;
      return { itemId, courseId: item?.course_id };
    },
    onMutate: async (itemId: string) => {
      await qc.cancelQueries({ queryKey: CART_KEY(user?.id) });
      const prev = qc.getQueryData<CartState>(CART_KEY(user?.id));
      if (prev) {
        qc.setQueryData<CartState>(CART_KEY(user?.id), {
          ...prev,
          items: prev.items.filter((i) => i.id !== itemId),
        });
      }
      return { prev };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(CART_KEY(user?.id), ctx.prev);
      toast({ title: "Não foi possível remover", variant: "destructive" });
    },
    onSuccess: ({ courseId }) => {
      if (courseId) {
        void logActivity("cart_remove", {
          action: "cart_remove",
          target_type: "course",
          target_id: courseId,
        });
      }
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      if (!user || !query.data?.cartId) return;
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("cart_id", query.data.cartId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CART_KEY(user?.id) });
      void logActivity("cart_clear", { action: "cart_clear" });
    },
  });

  const items = query.data?.items ?? [];
  const count = items.length;
  const subtotal = items.reduce(
    (sum, i) => sum + (i.course?.price ? Number(i.course.price) : 0),
    0
  );
  const isInCart = (courseId: string) => items.some((i) => i.course_id === courseId);

  return {
    cartId: query.data?.cartId ?? null,
    items,
    count,
    subtotal,
    isLoading: query.isLoading,
    isInCart,
    add: (courseId: string) => addItem.mutate(courseId),
    addMany: (courseIds: string[], bundleId?: string) =>
      addManyItems.mutate({ courseIds, bundleId }),
    addManyAsync: (courseIds: string[], bundleId?: string) =>
      addManyItems.mutateAsync({ courseIds, bundleId }),
    remove: (itemId: string) => removeItem.mutate(itemId),
    clear: () => clear.mutate(),
    isAdding: addItem.isPending || addManyItems.isPending,
  };
}
